import { ArrowDown, ArrowUp, TrendingUp } from "lucide-react";
import React, { useMemo } from "react";
import CanvasJSReact from "@canvasjs/react-charts";

const PerformanceSummary = ({
  data = [],
  kpis = [],
  rigName = null,
  showDefault = false,
  chartType = "column",
}) => {
  const { CanvasJSChart } = CanvasJSReact;

  console.log("PerformanceSummary received data:", data);
  console.log("PerformanceSummary showDefault:", showDefault);
  console.log("PerformanceSummary rigName:", rigName);
  console.log("PerformanceSummary chartType:", chartType);

  const isTimeBased = data.every((x) => !isNaN(new Date(x.x).getTime()));

  const detectMetricType = (value, label = "") => {
    // Check common unit patterns in the label
    const labelLower = label.toLowerCase();
    if (labelLower.includes("temperature") || labelLower.includes("temp"))
      return { type: "temperature", unit: "°C" };
    if (
      labelLower.includes("%") ||
      labelLower.includes("percent") ||
      labelLower.includes("level")
    )
      return { type: "percentage", unit: "%" };
    if (labelLower.includes("pressure"))
      return { type: "pressure", unit: "PSI" };
    if (labelLower.includes("voltage")) return { type: "voltage", unit: "V" };

    // Fallback to value-based detection
    if (value > -50 && value < 150) return { type: "temperature", unit: "°C" };
    if (value >= 0 && value <= 100) return { type: "percentage", unit: "%" };
    if (value >= 0) return { type: "number", unit: "" };
    return { type: "generic", unit: "" };
  }; // Transform API data into the format needed for the chart
  const formattedData = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) {
      // No data available - return empty array instead of hardcoded data
      return [];
    }

    // If data is in the new API format with chartType
    if (data[0]?.label && data[0]?.value) {
      const isTimeData = data.some((point) => {
        const labelDate = new Date(point.label);
        return labelDate instanceof Date && !isNaN(labelDate);
      });

      const metricType = detectMetricType(data[0].value, data[0].label);
      const metricName = data[0].metricName || data[0].label || "Value";

      // Use AI-selected chart type if available, otherwise detect based on data
      const chartType = data[0].chartType || (isTimeData ? "spline" : "column");

      return [
        {
          name: metricName,
          type: metricType.type,
          unit: metricType.unit,
          chartType: chartType,
          dataPoints: data.map((point) => ({
            x: point.x,
            y: point.value,
            label: point.label,
            color: point.color, // Support custom colors if provided
          })),
        },
      ];
    }

    // Handle existing time-series format
    if (data.length === 0) return [];
    const firstDataPoint = data[0];
    const metrics = Object.keys(firstDataPoint).filter(
      (key) =>
        key !== "timestamp" &&
        key !== "date" &&
        typeof firstDataPoint[key] === "number"
    );

    return metrics.map((metric) => {
      const sampleValue = data[0][metric];
      const metricType = detectMetricType(sampleValue, metric);
      return {
        name:
          metric.charAt(0).toUpperCase() + metric.slice(1).replace(/_/g, " "),
        type: metricType.type,
        unit: metricType.unit,
        chartType: "spline", // Default to spline for time-series data
        dataPoints: data.map((item) => ({
          x: new Date(item.timestamp || item.date),
          y: item[metric],
        })),
      };
    });
  }, [data]);

  // Define chart colors
  const seriesColors = [
    "#F97316", // orange
    "#3B82F6", // blue
    "#10B981", // green
    "#EAB308", // yellow
    "#8B5CF6", // purple
    "#EC4899", // pink
  ];
  const chartData = useMemo(() => {
    return formattedData.map((series, index) => ({
      type: series.chartType || data[0]?.chartType || chartType || "column",
      name: series.name,
      showInLegend: formattedData.length > 1,
      markerSize: 4,
      markerType: "circle",
      markerColor: seriesColors[index % seriesColors.length],
      color: seriesColors[index % seriesColors.length],
      lineColor: seriesColors[index % seriesColors.length],
      lineThickness: 2,
      dataPoints: series.dataPoints.map((point) => ({
        ...point,
        color: point.color || undefined, // Use custom color if provided
      })),
    }));
  }, [formattedData, chartType, data]);
  const chartOptions = {
    animationEnabled: true,
    backgroundColor: "transparent",
    theme: "dark2",
    height: 380,
    title: {
      text:
        data[0]?.metricName ||
        (rigName
          ? `${rigName} Performance Metrics`
          : showDefault
          ? "Assets Status Overview"
          : "Performance Metrics"),
      fontColor: "rgba(255,255,255,0.8)",
      fontSize: 16,
    },
    axisX: {
      labelFontColor: "rgba(255,255,255,0.5)",
      gridColor: "rgba(255,255,255,0.1)",
      lineColor: "rgba(255,255,255,0.1)",
      tickColor: "rgba(255,255,255,0.1)",
      title: data.length === 0 ? "Rigs" : "",
      titleFontColor: "rgba(255,255,255,0.7)",
    },
    axisY: {
      includeZero: true,
      gridColor: "rgba(255,255,255,0.1)",
      labelFontColor: "rgba(255,255,255,0.5)",
      lineColor: "rgba(255,255,255,0.1)",
      tickColor: "rgba(255,255,255,0.1)",
      suffix: formattedData[0]?.unit || "",
      title: formattedData[0]?.name || "Value",
      titleFontColor: "rgba(255,255,255,0.7)",
      minimum:
        data.length === 0 || !formattedData?.[0]?.dataPoints?.length
          ? 0
          : Math.floor(
              Math.min(...formattedData[0].dataPoints.map((dp) => dp.y)) -
                (Math.max(...formattedData[0].dataPoints.map((dp) => dp.y)) -
                  Math.min(...formattedData[0].dataPoints.map((dp) => dp.y))) *
                  0.1
            ),

      maximum:
        data.length === 0
          ? 100
          : Math.max(...(formattedData[0]?.dataPoints.map((x) => x.y) || [])) +
            10,
      crosshair: {
        enabled: true,
        snapToDataPoint: true,
        color: "rgba(255,255,255,0.3)",
      },
    },
    tooltip: {
      shared: true,
      backgroundColor: "#222222",
      borderColor: "rgba(255,255,255,0.1)",
      fontColor: "#fff",
      cornerRadius: 8,
      contentFormatter: function (e) {
        let content = `<strong>${
          e.entries[0].dataPoint.label || e.entries[0].dataPoint.x
        }</strong><br/>`;

        e.entries.forEach((entry) => {
          const unit =
            formattedData.find((s) => s.name === entry.dataSeries.name)?.unit ||
            "";
          const color = entry.dataPoint.color || entry.dataSeries.color;
          content += `<span style="color: ${color}">${
            entry.dataSeries.name
          }</span>: <strong>${entry.dataPoint.y.toFixed(
            2
          )}${unit}</strong><br/>`;
        });
        return content;
      },
    },
    legend: {
      fontColor: "rgba(255,255,255,0.7)",
      cursor: "pointer",
      itemclick: function (e) {
        if (
          typeof e.dataSeries.visible === "undefined" ||
          e.dataSeries.visible
        ) {
          e.dataSeries.visible = false;
        } else {
          e.dataSeries.visible = true;
        }
        e.chart.render();
      },
    },
    data: chartData,
  };
  if (formattedData.length === 0) {
    return (
      <div className="rounded-xl p-3 h-full border-neutral-800 border text-white overflow-hidden bg-gradient-to-br from-gray-900 to-gray-800 border-border/30 shadow-xl">
        <div className="flex flex-row items-center justify-between pb-2 h-full w-full">
          <div className="space-y-1 h-full w-full">
            <div className="text-xl flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-orange-500" />
              Performance Summary
            </div>
            <div className="text-sm text-neutral-400 h-full flex justify-center items-center">
              <div className="text-center">
                <p className="mb-2">No performance data available</p>
                <p className="text-xs text-gray-500">
                  Charts will appear when AI provides data visualization
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl p-3 h-full border-neutral-800 border text-white overflow-hidden bg-gradient-to-br from-gray-900 to-gray-800 border-border/30 shadow-xl">
      <div className="flex flex-row items-center justify-between pb-2">
        <div className="space-y-1">
          <div className="text-xl flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-orange-500" />
            Performance Summary
          </div>
          <div className="text-sm text-neutral-400">
            Performance metrics across assets
          </div>
        </div>
      </div>

      <div className="space-y-4 p-6 h-full pt-0">
        {" "}
        {/* Rig Monitoring KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {kpis.length > 0 ? (
            kpis.map((kpi, idx) => (
              <div
                key={idx}
                className="flex flex-col rounded-md p-3 bg-gray-800/50 border border-neutral-800 backdrop-blur-sm hover:bg-gray-800/80 transition-all"
              >
                <span className="text-sm text-neutral-400">{kpi.label}</span>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-medium text-white">
                    {kpi.value}
                  </span>
                  {typeof kpi.change === "number" && (
                    <div
                      className={`flex items-center text-xs ${
                        kpi.isPositive ? "text-green-500" : "text-red-500"
                      }`}
                    >
                      {kpi.isPositive ? (
                        <ArrowUp className="h-3 w-3" />
                      ) : (
                        <ArrowDown className="h-3 w-3" />
                      )}
                      <span>{Math.abs(kpi.change)}%</span>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <>
              <div className="flex flex-col rounded-md p-3 bg-gray-800/50 border border-red-800 backdrop-blur-sm">
                <span className="text-sm text-red-400">Critical Alarms</span>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-medium text-red-300">
                    7 Active
                  </span>
                  <div className="flex items-center text-xs text-red-500">
                    <ArrowUp className="h-3 w-3" />
                    <span>High</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col rounded-md p-3 bg-gray-800/50 border border-yellow-800 backdrop-blur-sm">
                <span className="text-sm text-yellow-400">Avg Fuel Level</span>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-medium text-yellow-300">
                    6.8%
                  </span>
                  <div className="flex items-center text-xs text-red-500">
                    <ArrowDown className="h-3 w-3" />
                    <span>Critical</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col rounded-md p-3 bg-gray-800/50 border border-orange-800 backdrop-blur-sm">
                <span className="text-sm text-orange-400">Avg Temperature</span>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-medium text-orange-300">
                    41.5°C
                  </span>
                  <div className="flex items-center text-xs text-green-500">
                    <ArrowUp className="h-3 w-3" />
                    <span>Normal</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col rounded-md p-3 bg-gray-800/50 border border-blue-800 backdrop-blur-sm">
                <span className="text-sm text-blue-400">Total Capacity</span>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-medium text-blue-300">
                    3600 kW
                  </span>
                  <div className="flex items-center text-xs text-green-500">
                    <ArrowUp className="h-3 w-3" />
                    <span>Available</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        <div
          className="w-full bg-gray-800/30 p-3 rounded-lg border border-neutral-800"
          style={{ minHeight: "100%" }}
        >
          <CanvasJSChart options={chartOptions} />
        </div>
      </div>
    </div>
  );
};

export default PerformanceSummary;
